import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit'
import { campusesAPI } from '@/api'
import { normalizeData } from '@/utils/jsonapi'

// Create entity adapter for campuses
const campusesAdapter = createEntityAdapter({
  selectId: (campus) => campus.id,
  sortComparer: (a, b) => (a.name || '').localeCompare(b.name || ''),
})

/**
 * Fetch all campuses with optional filters
 *
 * @param {Object} [queryOptions] - JSON:API query options
 * @param {Object} [queryOptions.filters] - Filter criteria (e.g., { is_active: true, name: { icontains: 'main' } })
 * @param {Array<string>|string} [queryOptions.sort] - Sort fields
 * @param {Array<string>|string} [queryOptions.include] - Relations to include
 * @param {number} [queryOptions.page] - Page number
 * @param {number} [queryOptions.pageSize] - Items per page
 */
export const fetchCampuses = createAsyncThunk(
  'campuses/fetchCampuses',
  async (queryOptions = {}, { rejectWithValue }) => {
    try {
      return await campusesAPI.getCampuses(queryOptions)
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to load campuses' }
      )
    }
  }
)

/**
 * Fetch single campus by ID
 */
export const fetchCampus = createAsyncThunk(
  'campuses/fetchCampus',
  async (id, { rejectWithValue }) => {
    try {
      return await campusesAPI.getCampus(id)
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to load campus' }
      )
    }
  }
)

/**
 * Create new campus
 */
export const createCampus = createAsyncThunk(
  'campuses/createCampus',
  async (campusData, { rejectWithValue }) => {
    try {
      return await campusesAPI.createCampus(campusData)
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to create campus' }
      )
    }
  }
)

/**
 * Update campus
 */
export const updateCampus = createAsyncThunk(
  'campuses/updateCampus',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      return await campusesAPI.updateCampus(id, updates)
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to update campus' }
      )
    }
  }
)

/**
 * Delete campus
 * Note: May fail if campus has dependencies
 */
export const deleteCampus = createAsyncThunk(
  'campuses/deleteCampus',
  async (id, { rejectWithValue }) => {
    try {
      await campusesAPI.deleteCampus(id)
      return id
    } catch (error) {
      // Return full error for dependency warnings
      return rejectWithValue(error.response?.data || error.apiError || {
        message: 'Failed to delete campus'
      })
    }
  }
)

/**
 * Activate campus
 */
export const activateCampus = createAsyncThunk(
  'campuses/activateCampus',
  async (id, { rejectWithValue }) => {
    try {
      return await campusesAPI.activateCampus(id)
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to activate campus' }
      )
    }
  }
)

/**
 * Deactivate campus
 */
export const deactivateCampus = createAsyncThunk(
  'campuses/deactivateCampus',
  async (id, { rejectWithValue }) => {
    try {
      return await campusesAPI.deactivateCampus(id)
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to deactivate campus' }
      )
    }
  }
)

const initialState = campusesAdapter.getInitialState({
  selectedCampus: null,
  loading: false,
  error: null,
  saveSuccess: false,
  // Track individual campus operations
  activating: {},
  deactivating: {},
})

const campusesSlice = createSlice({
  name: 'campuses',
  initialState,
  reducers: {
    setSelectedCampus: (state, action) => {
      state.selectedCampus = action.payload
    },
    clearSelectedCampus: (state) => {
      state.selectedCampus = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearSaveSuccess: (state) => {
      state.saveSuccess = false
    },
    upsertMany: (state, action) => {
      campusesAdapter.upsertMany(state, action.payload)
    },
  },
  extraReducers: (builder) => {
    // Fetch campuses
    builder
      .addCase(fetchCampuses.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCampuses.fulfilled, (state, action) => {
        state.loading = false
        // Use normalizeData utility for JSON:API normalization
        const campuses = normalizeData(action.payload.data || []);
        campusesAdapter.setAll(state, campuses);
      })
      .addCase(fetchCampuses.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to load campuses'
      })

    // Fetch single campus
    builder
      .addCase(fetchCampus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCampus.fulfilled, (state, action) => {
        state.loading = false
        state.selectedCampus = action.payload
      })
      .addCase(fetchCampus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to load campus'
      })

    // Create campus
    builder
      .addCase(createCampus.pending, (state) => {
        state.loading = true
        state.error = null
        state.saveSuccess = false
      })
      .addCase(createCampus.fulfilled, (state, action) => {
        state.loading = false
        campusesAdapter.addOne(state, action.payload)
        state.saveSuccess = true
      })
      .addCase(createCampus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to create campus'
      })

    // Update campus
    builder
      .addCase(updateCampus.pending, (state) => {
        state.loading = true
        state.error = null
        state.saveSuccess = false
      })
      .addCase(updateCampus.fulfilled, (state, action) => {
        state.loading = false
        campusesAdapter.upsertOne(state, action.payload)
        if (state.selectedCampus?.id === action.payload.id) {
          state.selectedCampus = action.payload
        }
        state.saveSuccess = true
      })
      .addCase(updateCampus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to update campus'
      })

    // Delete campus
    builder
      .addCase(deleteCampus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteCampus.fulfilled, (state, action) => {
        state.loading = false
        campusesAdapter.removeOne(state, action.payload)
        if (state.selectedCampus?.id === action.payload) {
          state.selectedCampus = null
        }
      })
      .addCase(deleteCampus.rejected, (state, action) => {
        state.loading = false
        // Store full error payload for dependency handling
        state.error = action.payload
      })

    // Activate campus
    builder
      .addCase(activateCampus.pending, (state, action) => {
        state.activating[action.meta.arg] = true
      })
      .addCase(activateCampus.fulfilled, (state, action) => {
        delete state.activating[action.meta.arg]
        campusesAdapter.upsertOne(state, action.payload)
        if (state.selectedCampus?.id === action.payload.id) {
          state.selectedCampus = action.payload
        }
      })
      .addCase(activateCampus.rejected, (state, action) => {
        delete state.activating[action.meta.arg]
        state.error = action.payload?.message || 'Failed to activate campus'
      })

    // Deactivate campus
    builder
      .addCase(deactivateCampus.pending, (state, action) => {
        state.deactivating[action.meta.arg] = true
      })
      .addCase(deactivateCampus.fulfilled, (state, action) => {
        delete state.deactivating[action.meta.arg]
        campusesAdapter.upsertOne(state, action.payload)
        if (state.selectedCampus?.id === action.payload.id) {
          state.selectedCampus = action.payload
        }
      })
      .addCase(deactivateCampus.rejected, (state, action) => {
        delete state.deactivating[action.meta.arg]
        state.error = action.payload?.message || 'Failed to deactivate campus'
      })
  },
})

export const {
  setSelectedCampus,
  clearSelectedCampus,
  clearError,
  clearSaveSuccess,
  upsertMany,
} = campusesSlice.actions

// Export adapter selectors
export const {
  selectAll: selectAllCampuses,
  selectById: selectCampusById,
} = campusesAdapter.getSelectors((state) => state.campuses || campusesAdapter.getInitialState())

// Aliases for backwards compatibility
export const selectCampuses = selectAllCampuses
export const fetchCampusesThunk = fetchCampuses

export default campusesSlice.reducer
